import { SignedIn, SignedOut } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";

import { AppCrashScreen } from "./observability/AppCrashScreen";
import { SentryIdentityBridge } from "./observability/SentryIdentityBridge";
import { WorkspaceShell } from "./shell/WorkspaceShell";
import { RegistryDrivenPage } from "./shell/RegistryDrivenPage";
import { newPendingRoutes } from "./navigation/registry";
import { ApexSignupResume } from "./components/ApexSignupResume";
import { ApiTokenBridge } from "./components/ApiTokenBridge";
import { OrgAutoSwitch } from "./components/OrgAutoSwitch";
import {
  AIModule,
  OperationsModule,
  PeopleModule,
  PoliciesModule,
  ReportsModule,
  SettingsModule,
} from "./components/ModuleShells";
import {
  AILandingPage,
  OperationsLandingPage,
  PeopleLandingPage,
  PoliciesLandingPage,
  ReportsLandingPage,
  SettingsLandingPage,
} from "./pages/landings";
import { SignedOutScreen } from "./components/SignedOutScreen";
import { ApproverProvider } from "./lib/approver";
import { LegalEntityProvider } from "./lib/legalEntity";
import { tenantContext } from "./lib/tenant";
import { AIConfigPage } from "./pages/AIConfigPage";
import { AttendanceGridPage } from "./pages/AttendanceGridPage";
import { Article107OtCalculatorPage } from "./pages/Article107OtCalculatorPage";
import { EosDetailPage } from "./pages/EosDetailPage";
import { EosListPage } from "./pages/EosListPage";
import { ExcuseQueuePage } from "./pages/ExcuseQueuePage";
import { PolicyDraftPage } from "./pages/PolicyDraftPage";
import { PolicyUploadPage } from "./pages/PolicyUploadPage";
import { ExcuseTypesPage } from "./pages/ExcuseTypesPage";
import { ShiftsCatalogPage } from "./pages/ShiftsCatalogPage";
import { ShiftRotationPatternsPage } from "./pages/ShiftRotationPatternsPage";
import { ShiftRosterPage } from "./pages/ShiftRosterPage";
import { EmployeeAttendanceHubPage } from "./pages/EmployeeAttendanceHubPage";
import { EmployeeDetailPage } from "./pages/EmployeeDetailPage";
import { EmployeeListPage } from "./pages/EmployeeListPage";
import { AllowanceDetailPage } from "./pages/AllowanceDetailPage";
import { AllowancesPage } from "./pages/AllowancesPage";
import { ApprovalChainsPage } from "./pages/ApprovalChainsPage";
import { BeneficiariesPage } from "./pages/BeneficiariesPage";
import { BenefitClaimsPage } from "./pages/BenefitClaimsPage";
import { BenefitEnrollmentsPage } from "./pages/BenefitEnrollmentsPage";
import { BenefitsCCHIPage } from "./pages/BenefitsCCHIPage";
import { BenefitsPlansPage } from "./pages/BenefitsPlansPage";
import { DependentVerificationsPage } from "./pages/DependentVerificationsPage";
import { ProviderInvoicesPage } from "./pages/ProviderInvoicesPage";
// M3X.REPORTS hotfix — BenefitsReportsPage deleted; legacy JSON-dump
// dashboards removed (dead code per spec; dashboards live at /reports/dashboards).
// Route now renders BenefitsReportsLandingPage via <ReportsDomainIndex>.
import { ProbationWatchPage } from "./pages/ProbationWatchPage";
import { AttendanceShiftRosterPage } from "./pages/AttendanceShiftRosterPage";
import { AttendanceOvertimeApprovalsPage } from "./pages/AttendanceOvertimeApprovalsPage";
import { AttendanceExceptionQueuePage } from "./pages/AttendanceExceptionQueuePage";
import { BulkAttendanceAdjustmentsPage } from "./pages/BulkAttendanceAdjustmentsPage";
import { BiometricSyncLogPage } from "./pages/BiometricSyncLogPage";
import { WfhRegisterPage } from "./pages/WfhRegisterPage";
import { EosCalculationDraftsPage } from "./pages/EosCalculationDraftsPage";
import { ExitClearanceCenterPage } from "./pages/ExitClearanceCenterPage";
import { EosGovFilingsQueuePage } from "./pages/EosGovFilingsQueuePage";
import { EosFinalSettlementsPage } from "./pages/EosFinalSettlementsPage";
import { PayrollPeriodLockPage } from "./pages/PayrollPeriodLockPage";
import { OffCycleDisbursementsPage } from "./pages/OffCycleDisbursementsPage";
import { PayslipDistributionPage } from "./pages/PayslipDistributionPage";
import { WpsMudadOutputPage } from "./pages/WpsMudadOutputPage";
import { GlJournalOutputPage } from "./pages/GlJournalOutputPage";
import { BankFileOutputPage } from "./pages/BankFileOutputPage";
import { PayrollPostHistoryPage } from "./pages/PayrollPostHistoryPage";
import { ActiveTravelersPage } from "./pages/ActiveTravelersPage";
import { TravelReconciliationPage } from "./pages/TravelReconciliationPage";
import { PerDiemDisbursementsPage } from "./pages/PerDiemDisbursementsPage";

// Session 3c — 27 Policies second-line pages
import { LeaveAccrualRulesPolicyPage } from "./pages/LeaveAccrualRulesPolicyPage";
import { LeaveEligibilityMatrixPolicyPage } from "./pages/LeaveEligibilityMatrixPolicyPage";
import { LeaveEncashmentRulesPolicyPage } from "./pages/LeaveEncashmentRulesPolicyPage";
import { LeaveCarryForwardRulesPolicyPage } from "./pages/LeaveCarryForwardRulesPolicyPage";
import { LeaveYearCalendarPolicyPage } from "./pages/LeaveYearCalendarPolicyPage";
import { LoanEligibilityRulesPolicyPage } from "./pages/LoanEligibilityRulesPolicyPage";
import { LoanArticle90ConfigPolicyPage } from "./pages/LoanArticle90ConfigPolicyPage";
import { LoanSettlementMethodsPolicyPage } from "./pages/LoanSettlementMethodsPolicyPage";
import { ShiftPatternsPolicyPage } from "./pages/ShiftPatternsPolicyPage";
import { ShiftOvertimeRulesPolicyPage } from "./pages/ShiftOvertimeRulesPolicyPage";
import { ShiftGraceRulesPolicyPage } from "./pages/ShiftGraceRulesPolicyPage";
import { ShiftBiometricDevicesPolicyPage } from "./pages/ShiftBiometricDevicesPolicyPage";
import { ShiftGeofencesPolicyPage } from "./pages/ShiftGeofencesPolicyPage";
import { PayrollEarningsCatalogPolicyPage } from "./pages/PayrollEarningsCatalogPolicyPage";
import { PayrollDeductionsCatalogPolicyPage } from "./pages/PayrollDeductionsCatalogPolicyPage";
import { PayrollCalendarsPolicyPage } from "./pages/PayrollCalendarsPolicyPage";
import { PayrollGosiRatesPolicyPage } from "./pages/PayrollGosiRatesPolicyPage";
import { PayrollGlMappingPolicyPage } from "./pages/PayrollGlMappingPolicyPage";
import { PayrollBankTemplatesPolicyPage } from "./pages/PayrollBankTemplatesPolicyPage";
import { PayrollTaxWithholdingPolicyPage } from "./pages/PayrollTaxWithholdingPolicyPage";
import { TravelCategoriesPolicyPage } from "./pages/TravelCategoriesPolicyPage";
import { TravelDestinationsPolicyPage } from "./pages/TravelDestinationsPolicyPage";
import { TravelAdvanceReconciliationPolicyPage } from "./pages/TravelAdvanceReconciliationPolicyPage";
import { TravelBookingClassPolicyPage } from "./pages/TravelBookingClassPolicyPage";
import { EosCalculationRulesPolicyPage } from "./pages/EosCalculationRulesPolicyPage";
import { EosTerminationReasonsPolicyPage } from "./pages/EosTerminationReasonsPolicyPage";
import { EosNoticePeriodPolicyPage } from "./pages/EosNoticePeriodPolicyPage";

// Session 3d — 31 /me employee surface pages
import { MyHubDashboardPage } from "./pages/MyHubDashboardPage";
import { MyRequestLeavePage } from "./pages/MyRequestLeavePage";
import { MyLeaveRequestsPage } from "./pages/MyLeaveRequestsPage";
import { MyLeaveBalancesPage } from "./pages/MyLeaveBalancesPage";
import { MyLeaveCalendarPage } from "./pages/MyLeaveCalendarPage";
import { MyLeaveAdjustmentsPage } from "./pages/MyLeaveAdjustmentsPage";
import { MyLatestPayslipPage } from "./pages/MyLatestPayslipPage";
import { MyPayslipArchivePage } from "./pages/MyPayslipArchivePage";
import { MyYtdStatementPage } from "./pages/MyYtdStatementPage";
import { MyDeductionsBreakdownPage } from "./pages/MyDeductionsBreakdownPage";
import { MyBankInfoPage } from "./pages/MyBankInfoPage";
import { MyGosiStatementPage } from "./pages/MyGosiStatementPage";
import { MyApplyLoanPage } from "./pages/MyApplyLoanPage";
import { MyLoanApplicationsPage } from "./pages/MyLoanApplicationsPage";
import { MyActiveLoansPage } from "./pages/MyActiveLoansPage";
import { MyLoanRepaymentSchedulePage } from "./pages/MyLoanRepaymentSchedulePage";
import { MyLoanSettlementCalcPage } from "./pages/MyLoanSettlementCalcPage";
import { MyLoanHistoryPage } from "./pages/MyLoanHistoryPage";
import { MyAttendanceTodayPage } from "./pages/MyAttendanceTodayPage";
import { MyAttendancePeriodPage } from "./pages/MyAttendancePeriodPage";
import { MyExcusesPage } from "./pages/MyExcusesPage";
import { MyOvertimePage } from "./pages/MyOvertimePage";
import { MyShiftPage } from "./pages/MyShiftPage";
import { MyRequestTravelPage } from "./pages/MyRequestTravelPage";
import { MyActiveTripPage } from "./pages/MyActiveTripPage";
import { MyTravelReconciliationPage } from "./pages/MyTravelReconciliationPage";
import { MyTravelHistoryPage } from "./pages/MyTravelHistoryPage";
import { MyDocumentsPage } from "./pages/MyDocumentsPage";
import { MyEosProjectionPage } from "./pages/MyEosProjectionPage";
import { MyProfilePage } from "./pages/MyProfilePage";
import { MyTeamPage } from "./pages/MyTeamPage";
import { NewJoinersPage } from "./pages/NewJoinersPage";
import { LeaversPage } from "./pages/LeaversPage";
import { LeaveAdjustmentsPage } from "./pages/operations/leave/LeaveAdjustmentsPage";
import { LeaveEncashmentPage } from "./pages/operations/leave/LeaveEncashmentPage";
import { LeaveCarryForwardPage } from "./pages/operations/leave/LeaveCarryForwardPage";
import { LeaveInquiriesPage } from "./pages/operations/leave/LeaveInquiriesPage";
import { LeaveBulkActionsPage } from "./pages/operations/leave/LeaveBulkActionsPage";
import { LoanDisbursementQueuePage } from "./pages/LoanDisbursementQueuePage";
import { ActiveLoansRegisterPage } from "./pages/ActiveLoansRegisterPage";
import { LoanSettlementsPage } from "./pages/LoanSettlementsPage";
import { LoanArrearsPage } from "./pages/LoanArrearsPage";
import { Article90WatchPage } from "./pages/Article90WatchPage";
import { ExcuseHistoryPage } from "./pages/ExcuseHistoryPage";
import { ExcuseQuotaWatchPage } from "./pages/ExcuseQuotaWatchPage";
import { AIDecisionRegistryPage } from "./pages/AIDecisionRegistryPage";
import { AIScannerHealthPage } from "./pages/AIScannerHealthPage";
import { AIReasoningTracesPage } from "./pages/AIReasoningTracesPage";
import { RenewalAndRetirementPage } from "./pages/RenewalAndRetirementPage";
import { BrandingSettingsPage } from "./pages/BrandingSettingsPage";
import { AIFeedbackPage } from "./pages/AIFeedbackPage";
import { ChatDraftsPage } from "./pages/ChatDraftsPage";
import { ChatThreadsPage } from "./pages/ChatThreadsPage";
import { SharedThreadPage } from "./pages/SharedThreadPage";
import {
  ComplianceReportPage,
  CompensationReportPage,
  HeadcountReportPage,
  TurnoverReportPage,
} from "./pages/reports";
import {
  AuditLogPage,
  NotificationsPage,
  UsersRolesPage,
} from "./pages/settings";
import { TenantSettingsPage } from "./pages/settings/TenantSettingsPage";
import { LegalEntitiesPage } from "./pages/settings/LegalEntitiesPage";
import { BillingPage } from "./pages/settings/BillingPage";
import { MemberDetailPage } from "./pages/settings/MemberDetailPage";
import { DashboardsIndexPage } from "./pages/reports/DashboardsIndexPage";
// M3X.REPORTS hotfix — LeavesReportsPage deleted; route now renders
// LeavesReportsLandingPage via <ReportsDomainIndex>.
import { LibraryByCutPage } from "./pages/reports/LibraryByCutPage";
import { LibraryByDomainPage } from "./pages/reports/LibraryByDomainPage";
import { MyHubPage } from "./pages/MyHubPage";
// M3X.REPORTS hotfix — LoansReportsPage deleted; route now renders
// LoansReportsLandingPage via <ReportsDomainIndex>.
import { LoansOperationsDashboardPage } from "./pages/LoansOperationsDashboardPage";
import { OnboardingJourneyPage } from "./pages/onboarding/OnboardingJourneyPage";
import {
  ApprovalsStep,
  CompanyStep,
  EmployeesStep,
  GoLiveStep,
  IntegrationsStep,
  JobsGradesStep,
  PoliciesStep,
  ShadowPayrollStep,
  WelcomeStep,
} from "./pages/onboarding/steps";
// M3X.REPORTS PR 14-18 — ComingSoonPages.tsx exports retired; the 4 routes
// now render the live library framework pages from ./pages/reports/LibraryPages.
import { ComplianceAlertsPage } from "./pages/ComplianceAlertsPage";
import { ContractDetailPage } from "./pages/ContractDetailPage";
import { ContractsListPage } from "./pages/ContractsListPage";
import { ApprovalsQueuePage } from "./pages/ApprovalsQueuePage";
import { OperationsHubPage } from "./pages/OperationsHubPage";
import { PayrollCandidatesPage } from "./pages/PayrollCandidatesPage";
import { EosPlaceholderPage } from "./pages/transactions/EosPlaceholderPage";
import { AttendanceHrInitiatePage } from "./pages/transactions/AttendanceHrInitiatePage";
import { ContractAmendmentHrInitiatePage } from "./pages/transactions/ContractAmendmentHrInitiatePage";
import { ExcuseHrInitiatePage } from "./pages/transactions/ExcuseHrInitiatePage";
import { LeaveHrInitiatePage } from "./pages/transactions/LeaveHrInitiatePage";
import { LoanHrInitiatePage } from "./pages/transactions/LoanHrInitiatePage";
import { OvertimeHrInitiatePage } from "./pages/transactions/OvertimeHrInitiatePage";
import { HolidayCalendarPage } from "./pages/HolidayCalendarPage";
import { EmployeeMasterFilePage } from "./pages/EmployeeMasterFilePage";
import { ImportSessionPage } from "./pages/ImportSessionPage";
import { ImportsPage } from "./pages/settings/ImportsPage";
import { AgentsPage } from "./pages/AgentsPage";
import { AgentProposalsPage } from "./pages/AgentProposalsPage";
import { CompliancePoliciesPage } from "./pages/CompliancePoliciesPage";
import { EosDisputePage } from "./pages/EosDisputePage";
import { EmployeeLifecycleAgentPage } from "./pages/EmployeeLifecycleAgentPage";
import { GovernmentLayerPage } from "./pages/GovernmentLayerPage";
import { LeavesAgentPage } from "./pages/LeavesAgentPage";
import { LegalEntityAgentPage } from "./pages/LegalEntityAgentPage";
import { LoansAgentPage } from "./pages/LoansAgentPage";
import { BusinessTravelAgentPage } from "./pages/BusinessTravelAgentPage";
import { CompMgmtAgentPage } from "./pages/CompMgmtAgentPage";
import { OrgDesignAgentPage } from "./pages/OrgDesignAgentPage";
import { TravelTicketsAgentPage } from "./pages/TravelTicketsAgentPage";
import { BenefitsAgentPage } from "./pages/BenefitsAgentPage";
import { GradingAgentPage } from "./pages/GradingAgentPage";
import { ReportsAgentPage } from "./pages/ReportsAgentPage";
import { TimeAttendanceAgentPage } from "./pages/TimeAttendanceAgentPage";
import { PayrollAgentPage } from "./pages/PayrollAgentPage";
import { StuckInboxPage } from "./pages/StuckInboxPage";
import { ExcuseTypeDetailPage } from "./pages/ExcuseTypeDetailPage";
import { JobDetailPage } from "./pages/JobDetailPage";
import { LeaveTypeDetailPage } from "./pages/LeaveTypeDetailPage";
import { LoanProductDetailPage } from "./pages/LoanProductDetailPage";
import { PositionDetailPage } from "./pages/PositionDetailPage";
import { ShiftDetailPage } from "./pages/ShiftDetailPage";
import { LeaveApprovalsPage } from "./pages/operations/leave/LeaveApprovalsPage";
import { LeaveCalendarPage } from "./pages/operations/leave/LeaveCalendarPage";
import { LeaveRequestsPage } from "./pages/operations/leave/LeaveRequestsPage";
import { LeaveTypesPage } from "./pages/LeaveTypesPage";
import { LoanApplicationsPage } from "./pages/LoanApplicationsPage";
import { LoanProductsPage } from "./pages/LoanProductsPage";
import { EosRateMatrixPage } from "./pages/EosRateMatrixPage";
import { Article80WizardPage } from "./pages/Article80WizardPage";
import { EosOnboardingAutoflushPage } from "./pages/EosOnboardingAutoflushPage";
import { BiometricMachinesPage } from "./pages/BiometricMachinesPage";
import { MyEndOfServiceHubPage } from "./pages/MyEndOfServiceHubPage";
import { RetirementScannerPage } from "./pages/RetirementScannerPage";
import { AbscondedChecklistPage } from "./pages/AbscondedChecklistPage";
// M3X.REPORTS hotfix — EosReportsPage deleted; route now renders
// EosReportsLandingPage via <ReportsDomainIndex>.
import {
  AgentsReportsLandingPage,
  AnalyticsReportsLandingPage,
  AttendanceReportsLandingPage,
  BenefitsReportsLandingPage,
  CompReportsLandingPage,
  ComplianceReportsLandingPage as ComplianceReportsListPage,
  EmployeeReportsLandingPage,
  EosReportsLandingPage,
  ExcusesReportsLandingPage,
  LeavesReportsLandingPage,
  LoansReportsLandingPage,
  OrgReportsLandingPage,
  PayrollReportsLandingPage,
  TravelReportsLandingPage,
} from "./pages/reports/DomainIndexPages";
import { DynamicReportPage } from "./pages/reports/DynamicReportPage";
import {
  CustomBuilderPage,
  ExportsLogPage,
  SavedReportsPage,
  ScheduledExportsPage,
} from "./pages/reports/LibraryPages";
// M3X.REPORTS hotfix — TravelReportsPage removed from travel/index.tsx;
// route /reports/travel now renders TravelReportsLandingPage via
// <ReportsDomainIndex>.
import {
  MyTravelHubPage,
  TravelRequestsHRPage,
} from "./pages/travel";
import { RequestTravelModalPage } from "./pages/travel/RequestTravelModalPage";
import { TravelPerDiemEditorFullPage } from "./pages/travel/TravelPerDiemEditorFullPage";
import { DeceasedWorkflowPage } from "./pages/DeceasedWorkflowPage";
import { MissionControlPage } from "./pages/MissionControlPage";
import { MyPaymentHubPage } from "./pages/MyPaymentHubPage";
import { RequestLoanWizardPage } from "./pages/RequestLoanWizardPage";
import { LoanDetailPageRich } from "./pages/LoanDetailPageRichPage";
import { MyLeaveHubPage } from "./pages/MyLeaveHubPage";
import { RequestLeaveWizardPage } from "./pages/RequestLeaveWizardPage";
import { LeaveDetailPageRich } from "./pages/operations/leave/LeaveDetailPageRichPage";
import { EmployeeNewPage } from "./pages/EmployeeNewPage";
import { CommissionStructuresPage } from "./pages/CommissionStructuresPage";
import { CompBandsPage } from "./pages/CompBandsPage";
import { CompChangeDetailPage } from "./pages/CompChangeDetailPage";
import { CompChangeNewPage } from "./pages/CompChangeNewPage";
import { CompChangesPage } from "./pages/CompChangesPage";
import { CompCycleDetailPage } from "./pages/CompCycleDetailPage";
import { CompCyclesPage } from "./pages/CompCyclesPage";
import { EmployeeCompHistoryPage } from "./pages/EmployeeCompHistoryPage";
import { GradeDetailPage } from "./pages/GradeDetailPage";
import { GradesPage } from "./pages/GradesPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import {
  IntegrationsDocsPage,
  McpBetaPage,
  PersonalAccessTokensPage,
  PowerBIIntegrationPage,
} from "./pages/settings/PlatformPages";
// M3X.GOV.5 hotfix — split into per-portal files under
// pages/settings/integrations-gov/ (was a single GovIntegrationsHub.tsx).
import { GosiPortalPage } from "./pages/settings/integrations-gov/GosiPortalPage";
import { GovIntegrationsLandingPage } from "./pages/settings/integrations-gov/GovIntegrationsLandingPage";
import { MudadPortalPage } from "./pages/settings/integrations-gov/MudadPortalPage";
import { MuqeemPortalPage } from "./pages/settings/integrations-gov/MuqeemPortalPage";
import { QiwaPortalPage } from "./pages/settings/integrations-gov/QiwaPortalPage";
import { LandingPage } from "./pages/LandingPage";
import { LeaveBalancesPage } from "./pages/operations/leave/LeaveBalancesPage";
import { LoanSchedulesPage } from "./pages/LoanSchedulesPage";
import { SetupRedirect } from "./pages/SetupRedirect";
import { JobsPage } from "./pages/JobsPage";
import { OrgChartPage } from "./pages/OrgChartPage";
import { PayrollPage } from "./pages/PayrollPage";
import { PositionsPage } from "./pages/PositionsPage";
import { LocalizedClerkProvider } from "./components/LocalizedClerkProvider";
import { SignupPage } from "./pages/SignupPage";
import { DataTableDemoPage } from "./pages/_dev/DataTableDemoPage";

// Vite inlines this at build time. Clerk publishable keys are designed
// to ship in the client bundle (the Clerk SDK exposes them to every
// browser by design — they're CDN-distributed, not a secret). We
// therefore safely fall back to a hardcoded test-instance key so the
// hosted demo works even when the Cloudflare build environment hasn't
// been configured with VITE_CLERK_PUBLISHABLE_KEY.
//
// Production cutover 2026-05-31 — moved from the dev instance
// `pro-muskox-84.clerk.accounts.dev` to the production instance at
// `clerk.solvait.ai`. Production cookies are scoped to `.solvait.ai`
// so the session survives the apex → subdomain hop after signup
// (the "second Clerk redirect" bug on dev). DNS for the production
// instance is in the `solvait.ai` zone — `accounts`, `clerk`,
// `clkmail`, `clk._domainkey`, `clk2._domainkey`. See
// docs/CLERK_PRODUCTION_MIGRATION.md.
//
// Workers Static Assets has no runtime env vars (it just serves
// files), so we hardcode the publishable key here instead of trying
// to wire VITE_CLERK_PUBLISHABLE_KEY through wrangler. Publishable
// keys are PUBLIC by Clerk's design (they ship in every browser
// bundle anyway), so this is the supported pattern.
const _DEFAULT_CLERK_KEY = "pk_live_Y2xlcmsuc29sdmFpdC5haSQ";

const clerkKey: string =
  (import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined) ??
  _DEFAULT_CLERK_KEY;

// Session 3a — paths with bespoke routes mounted above the
// RegistryDrivenPage catch-all. Filtered out of `newPendingRoutes()`
// so a single path doesn't double-mount.
const SESSION_3A_BESPOKE_PATHS: ReadonlySet<string> = new Set([
  "/people/probation-watch",
  "/people/new-joiners",
  "/people/leavers",
  "/operations/leave/adjustments",
  "/operations/leave/encashment",
  "/operations/leave/carry-forward",
  "/operations/leave/inquiries",
  "/operations/leave/bulk",
  "/operations/loans/disbursement-queue",
  "/operations/loans/active-register",
  "/operations/loans/settlements",
  "/operations/loans/arrears",
  "/operations/loans/article-90-watch",
  "/operations/excuses/history",
  "/operations/excuses/quota-watch",
  "/ai/decision-registry",
  "/ai/scanner-health",
  "/ai/reasoning",
  // Session 3b — 20 bespoke pages (Attendance · EOS · Payroll · Travel)
  "/operations/attendance/shift-roster",
  "/operations/attendance/overtime-approvals",
  "/operations/attendance/exception-queue",
  "/operations/attendance/bulk-adjustments",
  "/operations/attendance/biometric-sync",
  "/operations/attendance/wfh-register",
  "/operations/eos/calculation-drafts",
  "/operations/eos/clearance-center",
  "/operations/eos/gov-filings-queue",
  "/operations/eos/final-settlements",
  "/operations/payroll/period-lock",
  "/operations/payroll/off-cycle",
  "/operations/payroll/payslip-distribution",
  "/operations/payroll/wps",
  "/operations/payroll/gl-output",
  "/operations/payroll/bank-file",
  "/operations/payroll/post-history",
  "/operations/travel/active-travelers",
  "/operations/travel/reconciliation",
  "/operations/travel/per-diem-disbursements",
  // Session 3c — 27 Policies second-line
  "/policies/leave/accrual-rules",
  "/policies/leave/eligibility-matrix",
  "/policies/leave/encashment-rules",
  "/policies/leave/carry-forward-rules",
  "/policies/leave/year-calendar",
  "/policies/loans/eligibility-rules",
  "/policies/loans/article-90-config",
  "/policies/loans/settlement-methods",
  "/policies/shifts/patterns",
  "/policies/shifts/overtime-rules",
  "/policies/shifts/grace-rules",
  "/policies/shifts/biometric-devices",
  "/policies/shifts/geofences",
  "/policies/payroll/earnings-catalog",
  "/policies/payroll/deductions-catalog",
  "/policies/payroll/calendars",
  "/policies/payroll/gosi-rates",
  "/policies/payroll/gl-mapping",
  "/policies/payroll/bank-templates",
  "/policies/payroll/tax-withholding",
  "/policies/travel/categories",
  "/policies/travel/destinations",
  "/policies/travel/advance-reconciliation",
  "/policies/travel/booking-class-rules",
  "/policies/eos/calculation-rules",
  "/policies/eos/termination-reasons",
  "/policies/eos/notice-period-rules",
  // Session 3d — 31 /me employee surface
  "/me",
  "/me/leaves/request",
  "/me/leaves/my-requests",
  "/me/leaves/balances",
  "/me/leaves/calendar",
  "/me/leaves/adjustments",
  "/me/payments/latest",
  "/me/payments/archive",
  "/me/payments/ytd",
  "/me/payments/deductions",
  "/me/payments/bank",
  "/me/payments/gosi",
  "/me/loans/apply",
  "/me/loans/applications",
  "/me/loans/active",
  "/me/loans/schedule",
  "/me/loans/settlement-calc",
  "/me/loans/history",
  "/me/attendance/today",
  "/me/attendance/period",
  "/me/attendance/excuses",
  "/me/attendance/overtime",
  "/me/attendance/shift",
  "/me/travel/request",
  "/me/travel/active",
  "/me/travel/reconciliation",
  "/me/travel/history",
  "/me/documents",
  "/me/eos",
  "/me/profile",
  "/me/team",
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

export default function App() {
  // Apex (no tenant slug) → marketing landing + /signup.
  // Subdomain (riyadh.solvait.ai → slug "riyadh") → the workspace.
  const onApex = tenantContext().slug === null;

  return (
    <Sentry.ErrorBoundary
      fallback={({ eventId, resetError }) => (
        <AppCrashScreen
          eventId={typeof eventId === "string" ? eventId : undefined}
          resetError={resetError}
        />
      )}
      showDialog={false}
    >
      <LocalizedClerkProvider publishableKey={clerkKey}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            {/* Bridges Clerk's session token into our fetch API client so
                every backend call carries `Authorization: Bearer <jwt>`. */}
            <ApiTokenBridge />
            <ApexSignupResume />
            {/* When the URL is a tenant subdomain AND the user is a
                member of the matching Clerk org AND that org isn't
                already active, switch Clerk's active org to it. Prevents
                "personal-account-mode" users from hitting 401-everything
                on every tenant API call because their JWT has org_id:
                null. Reloads once after switching. */}
            <OrgAutoSwitch />
            {/* Pushes Clerk user id + tenant slug into the Sentry SDK
                so captured events are filterable by tenant. */}
            <SentryIdentityBridge />
          <Routes>
            {/* Public — anyone can hit /signup regardless of auth state.
                The page handles SignedIn (skip to company form) vs
                SignedOut (show Clerk SignUp) internally. */}
            <Route path="/signup/*" element={<SignupPage flow="signup" />} />
            <Route path="/signin/*" element={<SignupPage flow="signin" />} />

            {onApex ? (
              // No tenant in the hostname → render the marketing site
              // at the root. Other apex paths fall back to landing too;
              // workspace routes don't make sense without a tenant.
              <Route path="*" element={<LandingPage />} />
            ) : (
              // Tenant subdomain → the workspace, gated behind Clerk.
              <Route
                path="*"
                element={
                  <>
                    <SignedOut>
                      <SignedOutScreen />
                    </SignedOut>
                    <SignedIn>
                     <LegalEntityProvider>
                      <ApproverProvider>
                      <Routes>
                        <Route element={<WorkspaceShell />}>
                          {/* M3X.WORKSPACE-CLOSURE Session 2 PR 10 — the legacy
                              `AppShell` was deleted from disk. WorkspaceShell
                              is the only chrome. */}
                          {/* ---- Mission Control (default landing) ---- */}
                          <Route path="/" element={<Navigate to="/mission-control" replace />} />
                          <Route path="/mission-control" element={<MissionControlPage />} />

                          {/* ---- People module ----- */}
                          <Route element={<PeopleModule />}>
                            <Route path="/people" element={<PeopleLandingPage />} />
                            <Route path="/people/org-design-agent" element={<OrgDesignAgentPage />} />
                            <Route path="/people/lifecycle-agent" element={<EmployeeLifecycleAgentPage />} />
                            <Route path="/people/employees" element={<EmployeeListPage />} />
                            <Route path="/people/employees/new" element={<EmployeeNewPage />} />
                            <Route path="/people/employees/:employeeId" element={<EmployeeMasterFilePage />} />
                            <Route path="/people/employees/:employeeId/legacy" element={<EmployeeDetailPage />} />
                            <Route path="/people/positions" element={<PositionsPage />} />
                            <Route path="/people/positions/:positionId" element={<PositionDetailPage />} />
                            <Route path="/people/org-chart" element={<OrgChartPage />} />
                            <Route path="/people/compliance-alerts" element={<ComplianceAlertsPage />} />
                            <Route path="/people/jobs" element={<JobsPage />} />
                            <Route path="/people/jobs/:jobId" element={<JobDetailPage />} />
                            <Route path="/people/contracts" element={<ContractsListPage />} />
                            <Route path="/people/contracts/:contractId" element={<ContractDetailPage />} />
                            {/* Session 3a PR 3 — New Joiners bespoke
                                (replaces RegistryDrivenPage). */}
                            <Route path="/people/new-joiners" element={<NewJoinersPage />} />
                            {/* Session 3a PR 4 — Leavers bespoke. */}
                            <Route path="/people/leavers" element={<LeaversPage />} />
                          </Route>

                          {/* ---- Operations module ----- */}
                          <Route element={<OperationsModule />}>
                            <Route path="/operations" element={<OperationsLandingPage />} />
                            {/* M30.5.1 — HR Operations Hub + payroll-candidate queue + EOS placeholder. */}
                            <Route path="/operations/hub" element={<OperationsHubPage />} />
                            <Route path="/operations/payroll-candidates" element={<PayrollCandidatesPage />} />
                            <Route path="/operations/transactions/eos/new" element={<EosPlaceholderPage />} />
                            <Route path="/operations/transactions/leave/new" element={<LeaveHrInitiatePage />} />
                            <Route path="/operations/transactions/loan/new" element={<LoanHrInitiatePage />} />
                            <Route path="/operations/transactions/excuse/new" element={<ExcuseHrInitiatePage />} />
                            <Route path="/operations/transactions/attendance/new" element={<AttendanceHrInitiatePage />} />
                            <Route path="/operations/transactions/overtime/new" element={<OvertimeHrInitiatePage />} />
                            <Route path="/operations/transactions/contract-amendment/new" element={<ContractAmendmentHrInitiatePage />} />
                            <Route path="/operations/approvals" element={<ApprovalsQueuePage />} />
                            <Route path="/operations/payroll" element={<PayrollPage />} />
                            <Route path="/operations/payroll-agent" element={<PayrollAgentPage />} />
                            <Route path="/operations/eos-dispute" element={<EosDisputePage />} />
                            <Route path="/operations/ta-agent" element={<TimeAttendanceAgentPage />} />
                            <Route path="/operations/leaves-agent" element={<LeavesAgentPage />} />
                            <Route path="/operations/loans-agent" element={<LoansAgentPage />} />
                            <Route path="/operations/travel-agent" element={<BusinessTravelAgentPage />} />
                            <Route path="/operations/comp-agent" element={<CompMgmtAgentPage />} />
                            <Route path="/operations/tickets-agent" element={<TravelTicketsAgentPage />} />
                            <Route path="/operations/benefits-agent" element={<BenefitsAgentPage />} />

                            {/* Leave (M15) — primary at /operations/leave/* */}
                            <Route path="/operations/leave" element={<Navigate to="/operations/leave/requests" replace />} />
                            <Route path="/operations/leave/requests" element={<LeaveRequestsPage />} />
                            <Route path="/operations/leave/approvals" element={<LeaveApprovalsPage />} />
                            <Route path="/operations/leave/calendar" element={<LeaveCalendarPage />} />
                            <Route path="/operations/leave/balances" element={<LeaveBalancesPage />} />

                            {/* Session 3a PR 5 — Leave Adjustments. */}
                            <Route path="/operations/leave/adjustments" element={<LeaveAdjustmentsPage />} />
                            {/* Session 3a PR 6 — Leave Encashment (Rawatib). */}
                            <Route path="/operations/leave/encashment" element={<LeaveEncashmentPage />} />
                            {/* Session 3a PR 7 — Year-End Carry-Forward. */}
                            <Route path="/operations/leave/carry-forward" element={<LeaveCarryForwardPage />} />
                            {/* Session 3a PR 8 — Leave Inquiries (NL search). */}
                            <Route path="/operations/leave/inquiries" element={<LeaveInquiriesPage />} />
                            {/* Session 3a PR 9 — Bulk Leave Actions. */}
                            <Route path="/operations/leave/bulk" element={<LeaveBulkActionsPage />} />

                            {/* Loans (M16) */}
                            <Route path="/operations/loans" element={<LoansOperationsDashboardPage />} />
                            <Route path="/operations/loans/applications" element={<LoanApplicationsPage />} />
                            <Route path="/operations/loans/schedules" element={<LoanSchedulesPage />} />

                            {/* Session 3a PR 10 — Loan Disbursement Queue (Rawatib). */}
                            <Route path="/operations/loans/disbursement-queue" element={<LoanDisbursementQueuePage />} />
                            {/* Session 3a PR 11 — Active Loans Register. */}
                            <Route path="/operations/loans/active-register" element={<ActiveLoansRegisterPage />} />
                            {/* Session 3a PR 12 — Early Settlements (Rawatib). */}
                            <Route path="/operations/loans/settlements" element={<LoanSettlementsPage />} />
                            {/* Session 3a PR 13 — Loan Arrears (Mutaba'aa). */}
                            <Route path="/operations/loans/arrears" element={<LoanArrearsPage />} />
                            {/* Session 3a PR 14 — Article 90 Watch (Mutaba'aa). */}
                            <Route path="/operations/loans/article-90-watch" element={<Article90WatchPage />} />
                            {/* M16 loan detail by id — must come AFTER the
                                Session 3a static routes above so React
                                Router's static-wins-over-dynamic ranking
                                doesn't accidentally swallow them in the
                                Policies-module sibling block. */}
                            <Route path="/operations/loans/:loan_id" element={<LoanDetailPageRich />} />

                            {/* Time & Attendance (M17) */}
                            <Route path="/operations/attendance" element={<AttendanceGridPage />} />
                            <Route path="/operations/attendance/ot-calculator" element={<Article107OtCalculatorPage />} />
                            {/* M3X.UIPOLISH PR 18 — /me hub landing (Q1A). */}
                            <Route path="/me" element={<MyHubPage />} />
                            <Route path="/me/attendance" element={<EmployeeAttendanceHubPage />} />
                            <Route path="/operations/excuses" element={<Navigate to="/operations/excuses/queue" replace />} />
                            <Route path="/operations/excuses/queue" element={<ExcuseQueuePage />} />
                            {/* Session 3a PR 15 — Excuse History. */}
                            <Route path="/operations/excuses/history" element={<ExcuseHistoryPage />} />
                            {/* Session 3a PR 16 — Quota Watch (Mutaba'aa). */}
                            <Route path="/operations/excuses/quota-watch" element={<ExcuseQuotaWatchPage />} />

                            {/* EOS / Termination workflow (M18) */}
                            <Route path="/operations/eos" element={<EosListPage />} />
                            <Route path="/operations/eos/:terminationId" element={<EosDetailPage />} />
                            <Route path="/operations/import" element={<ImportSessionPage />} />

                            {/* M3X.WORKSPACE-CLOSURE Session 2 PR 1 — registry-aligned COMP routes.
                                Earlier they shipped under /operations/transactions/comp/* +
                                /operations/comp-cycle/* (singular). Registry uses the plural
                                /operations/comp-changes/* + /operations/comp-cycles/*. */}
                            <Route path="/operations/comp-changes" element={<CompChangesPage />} />
                            <Route path="/operations/comp-changes/new" element={<CompChangeNewPage />} />
                            <Route path="/operations/comp-changes/:id" element={<CompChangeDetailPage />} />
                            <Route path="/operations/comp-cycles" element={<CompCyclesPage />} />
                            <Route path="/operations/comp-cycles/:id" element={<CompCycleDetailPage />} />
                            <Route path="/operations/commission-structures" element={<CommissionStructuresPage />} />
                            <Route path="/operations/employees/:id/comp-history" element={<EmployeeCompHistoryPage />} />

                            {/* M3X.WORKSPACE-CLOSURE Session 2 PR 1 — 4 wire-orphan routes that
                                lived in the registry but had no Route entry. Per the spec's
                                step 5: if no page file exists, route to RegistryDrivenPage so
                                a fresh navigation doesn't land on a blank page. The salary
                                sheet redirects to the existing /operations/payroll surface. */}
                            <Route path="/operations/payroll/salary-sheet" element={<Navigate to="/operations/payroll" replace />} />
                            {/* /ai/reasoning relocated to the AI module
                                with its bespoke implementation in Session
                                3a PR 19. */}
                            <Route path="/settings/users-roles" element={<RegistryDrivenPage />} />
                            <Route path="/settings/statutory-defaults" element={<RegistryDrivenPage />} />
                          </Route>

                          {/* ---- Policies module ----- */}
                          <Route element={<PoliciesModule />}>
                            <Route path="/policies" element={<PoliciesLandingPage />} />
                            <Route path="/policies/compliance-rules" element={<CompliancePoliciesPage />} />
                            <Route path="/policies/government-layer" element={<GovernmentLayerPage />} />
                            <Route path="/policies/grading-agent" element={<GradingAgentPage />} />
                            <Route path="/policies/grades" element={<GradesPage />} />
                            <Route path="/policies/comp-bands" element={<CompBandsPage />} />
                            <Route path="/policies/commission-structures" element={<CommissionStructuresPage />} />
                            <Route path="/operations/transactions/comp" element={<CompChangesPage />} />
                            <Route path="/operations/transactions/comp/new" element={<CompChangeNewPage />} />
                            <Route path="/operations/transactions/comp/hr-initiate" element={<CompChangeNewPage />} />
                            <Route path="/operations/transactions/comp/:id" element={<CompChangeDetailPage />} />
                            <Route path="/people/employees/:id/comp-history" element={<EmployeeCompHistoryPage />} />
                            <Route path="/operations/comp-cycle" element={<CompCyclesPage />} />
                            <Route path="/operations/comp-cycle/:id" element={<CompCycleDetailPage />} />
                            <Route path="/policies/grades/:gradeId" element={<GradeDetailPage />} />
                            <Route path="/policies/allowances" element={<AllowancesPage />} />
                            <Route path="/policies/allowances/:allowanceId" element={<AllowanceDetailPage />} />
                            <Route path="/policies/benefits-plans" element={<BenefitsPlansPage />} />
                            <Route path="/compliance/benefits-cchi" element={<BenefitsCCHIPage />} />
                            <Route path="/operations/benefits-enrollments" element={<BenefitEnrollmentsPage />} />
                            <Route path="/operations/dependent-verifications" element={<DependentVerificationsPage />} />
                            <Route path="/people/beneficiaries" element={<BeneficiariesPage />} />
                            <Route path="/operations/provider-invoices" element={<ProviderInvoicesPage />} />
                            <Route path="/operations/benefit-claims" element={<BenefitClaimsPage />} />
                            <Route path="/operations/benefits-renewal" element={<RenewalAndRetirementPage />} />
                            <Route path="/reports/benefits" element={<BenefitsReportsLandingPage />} />
                            <Route path="/policies/leave-types" element={<LeaveTypesPage />} />
                            <Route path="/policies/leave-types/:leaveTypeId" element={<LeaveTypeDetailPage />} />
                            <Route path="/policies/loan-products" element={<LoanProductsPage />} />
                            <Route path="/policies/eos-rate-matrix" element={<EosRateMatrixPage />} />
                            <Route path="/operations/eos/article-80-classify" element={<Article80WizardPage />} />
                            <Route path="/policies/eos-rate-matrix/autoflush" element={<EosOnboardingAutoflushPage />} />
                            <Route path="/integrations/biometric" element={<BiometricMachinesPage />} />
                            <Route path="/me/end-of-service" element={<MyEndOfServiceHubPage />} />
                            <Route path="/operations/eos/retirement" element={<RetirementScannerPage />} />
                            <Route path="/operations/eos/absconded-checklist" element={<AbscondedChecklistPage />} />
                            <Route path="/operations/eos/deceased-workflow" element={<DeceasedWorkflowPage />} />
                            <Route path="/me/payment-hub" element={<MyPaymentHubPage />} />
                            <Route path="/me/loans" element={<MyPaymentHubPage />} />
                            <Route path="/me/loans/new" element={<RequestLoanWizardPage />} />
                            {/* /operations/loans/:loan_id moved to the
                                Operations module (after the bespoke
                                Session 3a routes) so the static routes
                                rank correctly. */}
                            <Route path="/me/leaves" element={<MyLeaveHubPage />} />
                            <Route path="/me/leaves/new" element={<RequestLeaveWizardPage />} />
                            <Route path="/operations/leaves/:leave_id" element={<LeaveDetailPageRich />} />
                            <Route path="/policies/loan-products/:loanProductId" element={<LoanProductDetailPage />} />
                            <Route path="/policies/shifts" element={<ShiftsCatalogPage />} />
                            <Route path="/policies/shifts/rotations" element={<ShiftRotationPatternsPage />} />
                            <Route path="/policies/shifts/roster" element={<ShiftRosterPage />} />
                            <Route path="/policies/shifts/:shiftId" element={<ShiftDetailPage />} />
                            <Route path="/policies/excuse-types" element={<ExcuseTypesPage />} />
                            <Route path="/policies/excuse-types/:excuseTypeId" element={<ExcuseTypeDetailPage />} />
                            <Route path="/policies/approval-chains" element={<ApprovalChainsPage />} />
                            <Route path="/policies/approval-chains/:workflowType" element={<ApprovalChainsPage />} />
                            {/* M19 AI Ingestion lives under Policies — its
                                output (Leave Types / Allowances / etc.) is
                                what users care about; the AI is the
                                mechanism. Old /ai/ingestion paths redirect. */}
                            <Route path="/policies/ai-ingestion" element={<PolicyUploadPage />} />
                            <Route path="/policies/ai-ingestion/drafts/:draftId" element={<PolicyDraftPage />} />
                            <Route path="/policies/holiday-calendar" element={<HolidayCalendarPage />} />
                          </Route>

                          {/* ---- AI module ----- */}
                          <Route element={<AIModule />}>
                            <Route path="/ai" element={<AILandingPage />} />
                            <Route path="/ai/agents" element={<AgentsPage />} />
                            <Route path="/ai/proposals" element={<AgentProposalsPage />} />
                            <Route path="/ai/stuck-inbox" element={<StuckInboxPage />} />
                            <Route path="/ai/chat-drafts" element={<ChatDraftsPage />} />
                            <Route path="/ai/threads" element={<ChatThreadsPage />} />
                            <Route path="/ai/feedback" element={<AIFeedbackPage />} />
                            <Route path="/ai/shared/:code" element={<SharedThreadPage />} />
                            {/* AI Configuration — one page, 5 canonical
                                deep-linkable URLs (one per tab). */}
                            <Route path="/ai/behavior" element={<AIConfigPage tab="behavior" />} />
                            <Route path="/ai/boundaries" element={<AIConfigPage tab="boundaries" />} />
                            <Route path="/ai/persona" element={<AIConfigPage tab="persona" />} />
                            <Route path="/ai/knowledge" element={<AIConfigPage tab="knowledge" />} />
                            <Route path="/ai/sandbox" element={<AIConfigPage tab="sandbox" />} />
                            {/* /ai/config is the legacy single-page URL —
                                land on Behavior so existing bookmarks
                                don't 404. */}
                            <Route path="/ai/config" element={<Navigate to="/ai/behavior" replace />} />
                            {/* AI Activity Log → Audit Log filtered to AI
                                actors (M21 surface already supports this). */}
                            <Route path="/ai/activity-log" element={<Navigate to="/settings/audit-log?actor=ai" replace />} />
                            <Route path="/ai/activity" element={<Navigate to="/ai/activity-log" replace />} />
                            {/* M19 ingestion redirects to its new home
                                under Policies. */}
                            <Route path="/ai/ingestion" element={<Navigate to="/policies/ai-ingestion" replace />} />
                            <Route path="/ai/ingestion/upload" element={<Navigate to="/policies/ai-ingestion" replace />} />
                            <Route path="/ai/ingestion/drafts/:draftId" element={<LegacyIngestionDraftRedirect />} />
                            {/* Session 3a PR 17 — AI Decision Registry. */}
                            <Route path="/ai/decision-registry" element={<AIDecisionRegistryPage />} />
                            {/* Session 3a PR 18 — AI Scanner Health. */}
                            <Route path="/ai/scanner-health" element={<AIScannerHealthPage />} />
                            {/* Session 3a PR 19 — AI Reasoning Traces
                                (activates the previously-orphan route). */}
                            <Route path="/ai/reasoning" element={<AIReasoningTracesPage />} />
                          </Route>

                          {/* ---- Reports module — landing page only ----- */}
                          <Route element={<ReportsModule />}>
                            <Route path="/reports" element={<ReportsLandingPage />} />
                            {/* M3X.UIPOLISH PR 10-11 — library views. */}
                            <Route
                              path="/reports/library/:domain"
                              element={<LibraryByDomainPage />}
                            />
                            <Route
                              path="/reports/browse/:axis"
                              element={<LibraryByCutPage />}
                            />
                            <Route path="/reports/agent" element={<ReportsAgentPage />} />
                            <Route path="/reports/dashboards" element={<DashboardsIndexPage />} />
                            {/* M3X.REPORTS PR 14-18 — replace the 4
                                coming-soon pages with the live library
                                framework. */}
                            <Route path="/reports/custom-builder" element={<CustomBuilderPage />} />
                            <Route path="/reports/saved" element={<SavedReportsPage />} />
                            <Route path="/reports/scheduled" element={<ScheduledExportsPage />} />
                            <Route path="/reports/exports" element={<ExportsLogPage />} />
                            <Route path="/reports/exports-log" element={<ExportsLogPage />} />
                            {/* Legacy /reports/custom alias from M21 tabs. */}
                            <Route path="/reports/custom" element={<Navigate to="/reports/custom-builder" replace />} />
                            <Route path="/reports/headcount" element={<HeadcountReportPage />} />
                            <Route path="/reports/turnover" element={<TurnoverReportPage />} />
                            <Route path="/reports/compensation" element={<CompensationReportPage />} />
                            <Route path="/reports/compliance" element={<ComplianceReportPage />} />
                            <Route path="/reports/loans" element={<LoansReportsLandingPage />} />
                            <Route path="/reports/leaves" element={<LeavesReportsLandingPage />} />
                            <Route path="/reports/eos" element={<EosReportsLandingPage />} />
                            <Route path="/reports/travel" element={<TravelReportsLandingPage />} />
                            {/* M3X.REPORTS PR 3.5 — 9 new domain index pages
                                via the <ReportsDomainIndex /> template;
                                content populates as PR 4-13 add to the catalog. */}
                            <Route path="/reports/payroll" element={<PayrollReportsLandingPage />} />
                            <Route path="/reports/excuses" element={<ExcusesReportsLandingPage />} />
                            <Route path="/reports/attendance" element={<AttendanceReportsLandingPage />} />
                            <Route path="/reports/comp" element={<CompReportsLandingPage />} />
                            <Route path="/reports/employee" element={<EmployeeReportsLandingPage />} />
                            <Route path="/reports/org" element={<OrgReportsLandingPage />} />
                            <Route path="/reports/analytics" element={<AnalyticsReportsLandingPage />} />
                            <Route path="/reports/agents" element={<AgentsReportsLandingPage />} />
                            <Route path="/reports/compliance-list" element={<ComplianceReportsListPage />} />
                            {/* M3X.REPORTS PR 4-13 — every catalogued
                                report renders through DynamicReportPage,
                                which resolves the manifest + builds the
                                ReportConfig. ~150 reports route here. */}
                            <Route
                              path="/reports/:domain/:reportId"
                              element={<DynamicReportPage />}
                            />
                            <Route path="/me/travel" element={<MyTravelHubPage />} />
                            <Route path="/me/travel/new" element={<RequestTravelModalPage />} />
                            <Route path="/operations/transactions/travel" element={<TravelRequestsHRPage />} />
                            <Route path="/policies/travel-per-diem" element={<TravelPerDiemEditorFullPage />} />

                            {/* Session 3a PR 2 — Probation Watch
                                (bespoke replaces RegistryDrivenPage) */}
                            <Route path="/people/probation-watch" element={<ProbationWatchPage />} />

                            {/* Session 3b PR 1 — Shift Roster (Tashghil) */}
                            <Route path="/operations/attendance/shift-roster" element={<AttendanceShiftRosterPage />} />

                            {/* Session 3b PR 2 — OT Approvals (Tashghil) */}
                            <Route path="/operations/attendance/overtime-approvals" element={<AttendanceOvertimeApprovalsPage />} />

                            {/* Session 3b PR 3 — Exception Queue (Mutaba'aa) */}
                            <Route path="/operations/attendance/exception-queue" element={<AttendanceExceptionQueuePage />} />

                            {/* Session 3b PR 4 — Bulk Attendance Adjustments (Tashghil) */}
                            <Route path="/operations/attendance/bulk-adjustments" element={<BulkAttendanceAdjustmentsPage />} />

                            {/* Session 3b PR 5 — Biometric Sync Log (Tashghil) */}
                            <Route path="/operations/attendance/biometric-sync" element={<BiometricSyncLogPage />} />

                            {/* Session 3b PR 6 — WFH Register (Tashghil) */}
                            <Route path="/operations/attendance/wfh-register" element={<WfhRegisterPage />} />

                            {/* Session 3b PR 7 — EOS Calculation Drafts (Rawatib) */}
                            <Route path="/operations/eos/calculation-drafts" element={<EosCalculationDraftsPage />} />

                            {/* Session 3b PR 8 — Exit Clearance Center (Tashghil) */}
                            <Route path="/operations/eos/clearance-center" element={<ExitClearanceCenterPage />} />

                            {/* Session 3b PR 9 — Gov Filings Queue (Mutaba'aa) */}
                            <Route path="/operations/eos/gov-filings-queue" element={<EosGovFilingsQueuePage />} />

                            {/* Session 3b PR 10 — Final Settlements (Rawatib) */}
                            <Route path="/operations/eos/final-settlements" element={<EosFinalSettlementsPage />} />

                            {/* Session 3b PR 11 — Period Lock & Post (Rawatib · KEYSTONE) */}
                            <Route path="/operations/payroll/period-lock" element={<PayrollPeriodLockPage />} />

                            {/* Session 3b PR 12 — Off-Cycle Disbursements (Rawatib) */}
                            <Route path="/operations/payroll/off-cycle" element={<OffCycleDisbursementsPage />} />

                            {/* Session 3b PR 13 — Payslip Distribution (Khidmat) */}
                            <Route path="/operations/payroll/payslip-distribution" element={<PayslipDistributionPage />} />

                            {/* Session 3b PR 14 — WPS / Mudad Output (Rawatib) */}
                            <Route path="/operations/payroll/wps" element={<WpsMudadOutputPage />} />

                            {/* Session 3b PR 15 — GL Journal Output (Rawatib) */}
                            <Route path="/operations/payroll/gl-output" element={<GlJournalOutputPage />} />

                            {/* Session 3b PR 16 — Bank File Output (Rawatib) */}
                            <Route path="/operations/payroll/bank-file" element={<BankFileOutputPage />} />

                            {/* Session 3b PR 17 — Payroll Post History (Rawatib · KEYSTONE) */}
                            <Route path="/operations/payroll/post-history" element={<PayrollPostHistoryPage />} />

                            {/* Session 3b PR 18 — Active Travelers (Tashghil) */}
                            <Route path="/operations/travel/active-travelers" element={<ActiveTravelersPage />} />

                            {/* Session 3b PR 19 — Travel Reconciliation (Rawatib) */}
                            <Route path="/operations/travel/reconciliation" element={<TravelReconciliationPage />} />

                            {/* Session 3b PR 20 — Per Diem Disbursements (Rawatib) */}
                            <Route path="/operations/travel/per-diem-disbursements" element={<PerDiemDisbursementsPage />} />

                            {/* Session 3c — 27 Policies second-line pages */}
                            <Route path="/policies/leave/accrual-rules" element={<LeaveAccrualRulesPolicyPage />} />
                            <Route path="/policies/leave/eligibility-matrix" element={<LeaveEligibilityMatrixPolicyPage />} />
                            <Route path="/policies/leave/encashment-rules" element={<LeaveEncashmentRulesPolicyPage />} />
                            <Route path="/policies/leave/carry-forward-rules" element={<LeaveCarryForwardRulesPolicyPage />} />
                            <Route path="/policies/leave/year-calendar" element={<LeaveYearCalendarPolicyPage />} />
                            <Route path="/policies/loans/eligibility-rules" element={<LoanEligibilityRulesPolicyPage />} />
                            <Route path="/policies/loans/article-90-config" element={<LoanArticle90ConfigPolicyPage />} />
                            <Route path="/policies/loans/settlement-methods" element={<LoanSettlementMethodsPolicyPage />} />
                            <Route path="/policies/shifts/patterns" element={<ShiftPatternsPolicyPage />} />
                            <Route path="/policies/shifts/overtime-rules" element={<ShiftOvertimeRulesPolicyPage />} />
                            <Route path="/policies/shifts/grace-rules" element={<ShiftGraceRulesPolicyPage />} />
                            <Route path="/policies/shifts/biometric-devices" element={<ShiftBiometricDevicesPolicyPage />} />
                            <Route path="/policies/shifts/geofences" element={<ShiftGeofencesPolicyPage />} />
                            <Route path="/policies/payroll/earnings-catalog" element={<PayrollEarningsCatalogPolicyPage />} />
                            <Route path="/policies/payroll/deductions-catalog" element={<PayrollDeductionsCatalogPolicyPage />} />
                            <Route path="/policies/payroll/calendars" element={<PayrollCalendarsPolicyPage />} />
                            <Route path="/policies/payroll/gosi-rates" element={<PayrollGosiRatesPolicyPage />} />
                            <Route path="/policies/payroll/gl-mapping" element={<PayrollGlMappingPolicyPage />} />
                            <Route path="/policies/payroll/bank-templates" element={<PayrollBankTemplatesPolicyPage />} />
                            <Route path="/policies/payroll/tax-withholding" element={<PayrollTaxWithholdingPolicyPage />} />
                            <Route path="/policies/travel/categories" element={<TravelCategoriesPolicyPage />} />
                            <Route path="/policies/travel/destinations" element={<TravelDestinationsPolicyPage />} />
                            <Route path="/policies/travel/advance-reconciliation" element={<TravelAdvanceReconciliationPolicyPage />} />
                            <Route path="/policies/travel/booking-class-rules" element={<TravelBookingClassPolicyPage />} />
                            <Route path="/policies/eos/calculation-rules" element={<EosCalculationRulesPolicyPage />} />
                            <Route path="/policies/eos/termination-reasons" element={<EosTerminationReasonsPolicyPage />} />
                            <Route path="/policies/eos/notice-period-rules" element={<EosNoticePeriodPolicyPage />} />

                            {/* Session 3d — 31 /me employee surface pages */}
                            <Route path="/me" element={<MyHubDashboardPage />} />
                            <Route path="/me/leaves/request" element={<MyRequestLeavePage />} />
                            <Route path="/me/leaves/my-requests" element={<MyLeaveRequestsPage />} />
                            <Route path="/me/leaves/balances" element={<MyLeaveBalancesPage />} />
                            <Route path="/me/leaves/calendar" element={<MyLeaveCalendarPage />} />
                            <Route path="/me/leaves/adjustments" element={<MyLeaveAdjustmentsPage />} />
                            <Route path="/me/payments/latest" element={<MyLatestPayslipPage />} />
                            <Route path="/me/payments/archive" element={<MyPayslipArchivePage />} />
                            <Route path="/me/payments/ytd" element={<MyYtdStatementPage />} />
                            <Route path="/me/payments/deductions" element={<MyDeductionsBreakdownPage />} />
                            <Route path="/me/payments/bank" element={<MyBankInfoPage />} />
                            <Route path="/me/payments/gosi" element={<MyGosiStatementPage />} />
                            <Route path="/me/loans/apply" element={<MyApplyLoanPage />} />
                            <Route path="/me/loans/applications" element={<MyLoanApplicationsPage />} />
                            <Route path="/me/loans/active" element={<MyActiveLoansPage />} />
                            <Route path="/me/loans/schedule" element={<MyLoanRepaymentSchedulePage />} />
                            <Route path="/me/loans/settlement-calc" element={<MyLoanSettlementCalcPage />} />
                            <Route path="/me/loans/history" element={<MyLoanHistoryPage />} />
                            <Route path="/me/attendance/today" element={<MyAttendanceTodayPage />} />
                            <Route path="/me/attendance/period" element={<MyAttendancePeriodPage />} />
                            <Route path="/me/attendance/excuses" element={<MyExcusesPage />} />
                            <Route path="/me/attendance/overtime" element={<MyOvertimePage />} />
                            <Route path="/me/attendance/shift" element={<MyShiftPage />} />
                            <Route path="/me/travel/request" element={<MyRequestTravelPage />} />
                            <Route path="/me/travel/active" element={<MyActiveTripPage />} />
                            <Route path="/me/travel/reconciliation" element={<MyTravelReconciliationPage />} />
                            <Route path="/me/travel/history" element={<MyTravelHistoryPage />} />
                            <Route path="/me/documents" element={<MyDocumentsPage />} />
                            <Route path="/me/eos" element={<MyEosProjectionPage />} />
                            <Route path="/me/profile" element={<MyProfilePage />} />
                            <Route path="/me/team" element={<MyTeamPage />} />

                            {/* M3X.WORKSPACE F.23-F.30 + F.31-F.40 — all
                                new-pending registry routes render the
                                RegistryDrivenPage (PageHeader + filter row
                                + DataTable shell + AI brief slot). Each
                                route gets a dedicated implementation in a
                                follow-up sprint; for now the menu walk
                                reaches every screen and the chassis is
                                exercised end-to-end. */}
                            {newPendingRoutes()
                              .filter((route) => !SESSION_3A_BESPOKE_PATHS.has(route))
                              .map((route) => (
                                <Route
                                  key={route}
                                  path={route}
                                  element={<RegistryDrivenPage />}
                                />
                              ))}
                          </Route>

                          {/* ---- Settings module ----- */}
                          <Route element={<SettingsModule />}>
                            <Route path="/settings" element={<SettingsLandingPage />} />
                            {/* Setup tab — mounts the M27 onboarding
                                journey index inside the Settings
                                module so users who hid the Mission
                                Control widget still have a clear
                                entry point. Same component as
                                /onboarding. */}
                            <Route path="/settings/setup" element={<OnboardingJourneyPage />} />
                            <Route path="/settings/tenant" element={<TenantSettingsPage />} />
                            <Route path="/settings/legal-entities" element={<LegalEntitiesPage />} />
                            <Route path="/settings/legal-entity-agent" element={<LegalEntityAgentPage />} />
                            <Route path="/settings/billing" element={<BillingPage />} />
                            <Route path="/settings/branding" element={<BrandingSettingsPage />} />
                            <Route path="/settings/integrations" element={<IntegrationsPage />} />
                            {/* M3X.PLATFORM-MINIMAL PR 10-11 */}
                            <Route
                              path="/settings/security/personal-access-tokens"
                              element={<PersonalAccessTokensPage />}
                            />
                            <Route
                              path="/settings/integrations/power-bi"
                              element={<PowerBIIntegrationPage />}
                            />
                            <Route
                              path="/settings/integrations/docs"
                              element={<IntegrationsDocsPage />}
                            />
                            <Route
                              path="/settings/integrations/mcp-beta"
                              element={<McpBetaPage />}
                            />
                            {/* M3X.GOV — Integration Hub + 4 portal pages */}
                            <Route
                              path="/settings/integrations/gov"
                              element={<GovIntegrationsLandingPage />}
                            />
                            <Route
                              path="/settings/integrations/gov/mudad"
                              element={<MudadPortalPage />}
                            />
                            <Route
                              path="/settings/integrations/gov/gosi"
                              element={<GosiPortalPage />}
                            />
                            <Route
                              path="/settings/integrations/gov/qiwa"
                              element={<QiwaPortalPage />}
                            />
                            <Route
                              path="/settings/integrations/gov/muqeem"
                              element={<MuqeemPortalPage />}
                            />
                            <Route path="/settings/users" element={<UsersRolesPage />} />
                            <Route
                              path="/settings/users/:user_id"
                              element={<MemberDetailPage />}
                            />
                            <Route path="/settings/notifications" element={<NotificationsPage />} />
                            <Route path="/settings/audit-log" element={<AuditLogPage />} />
                            <Route path="/settings/imports" element={<ImportsPage />} />
                          </Route>

                          {/* Legacy M7 /setup → M27 /onboarding redirects */}
                          <Route path="/setup/*" element={<SetupRedirect />} />

                          {/* ---- M27 post-signup Onboarding Journey ---- */}
                          <Route path="/onboarding" element={<OnboardingJourneyPage />} />
                          <Route path="/onboarding/welcome" element={<WelcomeStep />} />
                          <Route path="/onboarding/company" element={<CompanyStep />} />
                          <Route path="/onboarding/policies" element={<PoliciesStep />} />
                          <Route path="/onboarding/jobs-grades" element={<JobsGradesStep />} />
                          <Route path="/onboarding/employees" element={<EmployeesStep />} />
                          <Route path="/onboarding/approvals" element={<ApprovalsStep />} />
                          <Route path="/onboarding/integrations" element={<IntegrationsStep />} />
                          <Route path="/onboarding/shadow-payroll" element={<ShadowPayrollStep />} />
                          <Route path="/onboarding/go-live" element={<GoLiveStep />} />

                          {/* ---- M15+M16 legacy URLs — redirect to new module paths ---- */}
                          <Route path="/leave/types" element={<Navigate to="/policies/leave-types" replace />} />
                          <Route path="/leave/requests" element={<Navigate to="/operations/leave/requests" replace />} />
                          <Route path="/leave/approvals" element={<Navigate to="/operations/leave/approvals" replace />} />
                          <Route path="/leave/calendar" element={<Navigate to="/operations/leave/calendar" replace />} />
                          <Route path="/loans/products" element={<Navigate to="/policies/loan-products" replace />} />
                          <Route path="/loans/applications" element={<Navigate to="/operations/loans/applications" replace />} />

                          {/* ---- Dev/demo routes ---- */}
                          <Route path="/_dev/datatable-demo" element={<DataTableDemoPage />} />

                          {/* ---- Legacy URL redirects so existing bookmarks still work ---- */}
                          <Route path="/employees" element={<Navigate to="/people/employees" replace />} />
                          <Route path="/employees/new" element={<Navigate to="/people/employees/new" replace />} />
                          <Route path="/employees/:employeeId" element={<LegacyEmployeeRedirect />} />
                          <Route path="/positions" element={<Navigate to="/people/positions" replace />} />
                          <Route path="/org-chart" element={<Navigate to="/people/org-chart" replace />} />
                          <Route path="/jobs" element={<Navigate to="/people/jobs" replace />} />
                          <Route path="/contracts/:contractId" element={<LegacyContractRedirect />} />
                          <Route path="/payroll" element={<Navigate to="/operations/payroll" replace />} />
                          <Route path="/grades" element={<Navigate to="/policies/grades" replace />} />
                          <Route path="/grades/:gradeId" element={<LegacyGradeRedirect />} />
                          <Route path="/allowances" element={<Navigate to="/policies/allowances" replace />} />
                          <Route path="/ai-config" element={<Navigate to="/ai/config" replace />} />
                          <Route path="/integrations" element={<Navigate to="/settings/integrations" replace />} />
                        </Route>
                      </Routes>
                      </ApproverProvider>
                     </LegalEntityProvider>
                    </SignedIn>
                  </>
                }
              />
            )}
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </LocalizedClerkProvider>
    </Sentry.ErrorBoundary>
  );
}

// ---------------------------------------------------------------------------
// Helpers — legacy URL redirects with path params
// ---------------------------------------------------------------------------

function LegacyEmployeeRedirect() {
  const { employeeId } = useParams();
  return (
    <Navigate to={`/people/employees/${employeeId}`} replace />
  );
}

function LegacyContractRedirect() {
  const { contractId } = useParams();
  return (
    <Navigate to={`/people/contracts/${contractId}`} replace />
  );
}

function LegacyGradeRedirect() {
  const { gradeId } = useParams();
  return <Navigate to={`/policies/grades/${gradeId}`} replace />;
}

function LegacyIngestionDraftRedirect() {
  const { draftId } = useParams();
  return (
    <Navigate to={`/policies/ai-ingestion/drafts/${draftId}`} replace />
  );
}

// `ReportsPlaceholder` was replaced by `ReportsLandingPage` inside the
// `ReportsModule` shell as part of PR 3 (DESIGN_SYSTEM.md §1 "Module
// landing pattern").
